// Boot-time: resolve identity (authed vs anon), ensure user exists on backend,
// flush pending queues, sync authoritative user state (suspects + reaction count).
import api from "./api";
import storage from "./storage";

let bootPromise = null;
let identityPromise = null;

/**
 * Resolve the canonical user id for THIS session ONCE.
 * - If a cookie session is valid, /api/auth/me wins (identity = authed uid).
 *   authMe() also mirrors the id into storage so getEffectiveUserId() picks it up.
 * - Otherwise we fall through to the anonymous UUID (which is created on demand).
 * All downstream user-scoped calls MUST await this promise (via bootSync) or
 * call storage.getEffectiveUserId() AFTER it has settled.
 */
export function resolveIdentity() {
  if (identityPromise) return identityPromise;
  identityPromise = (async () => {
    try {
      const me = await api.authMe();
      if (me?.id) return me.id;
    } catch { /* not signed in — anon path */ }
    return storage.getEffectiveUserId();
  })();
  return identityPromise;
}

export function resetIdentity() { identityPromise = null; }

async function flushPendingScans(userId) {
  const scans = storage.drainScans();
  for (const s of scans) {
    try {
      await api.createScan({ user_id: userId, product_id: s.product_id });
    } catch (e) {
      storage.queueScan(s); // requeue
      throw e;
    }
  }
}

async function flushPendingReactions(userId) {
  const rxs = storage.drainReactions();
  for (const r of rxs) {
    try {
      await api.createReaction({
        user_id: userId,
        product_id: r.product_id,
        reaction_type: r.reaction_type,
        severity: r.severity,
      });
    } catch (e) {
      storage.queueReaction(r);
      throw e;
    }
  }
}

/** Fetch authoritative state from server and broadcast it to all components. */
export async function syncUserState(userId) {
  try {
    const [suspects, countRes, scansRes] = await Promise.all([
      api.listSuspects(userId, 10).catch(() => []),
      api.reactionsCount(userId).catch(() => ({ count: null })),
      api.getScansCount(userId).catch(() => ({ count: null })),
    ]);
    const serverCount = typeof countRes?.count === "number" ? countRes.count : null;
    const scanCount = typeof scansRes?.count === "number" ? scansRes.count : null;

    // Supabase is source of truth for unlock conditions. Mirror into localStorage.
    if (Array.isArray(suspects)) {
      storage.setSuspectCache(suspects);
    }
    if (serverCount !== null) {
      storage.setServerReactionCount(serverCount);
    }
    if (scanCount !== null || serverCount !== null || Array.isArray(suspects)) {
      storage.setCounters({
        scans: scanCount ?? storage.getCounters().scans ?? 0,
        reactions: serverCount ?? storage.getCounters().reactions ?? 0,
        suspects: Array.isArray(suspects) ? suspects.length : (storage.getCounters().suspects ?? 0),
      });
    }

    const detail = {
      // Include uid so listeners can ignore stale broadcasts fired for a
      // different account (e.g. a race between the anon bootSync and the
      // post-login re-sync).
      uid: userId,
      suspects: suspects || [],
      suspectCount: Array.isArray(suspects) ? suspects.length : 0,
      reactionCount: serverCount != null ? serverCount : storage.getServerReactionCount(),
      scanCount: scanCount != null ? scanCount : (storage.getCounters().scans ?? 0),
    };

    // Broadcast both events so legacy and new listeners pick up.
    window.dispatchEvent(new CustomEvent("userStateSync", { detail }));
    window.dispatchEvent(new CustomEvent("statsUpdated", { detail }));

    return detail;
  } catch {
    return {
      uid: userId,
      suspects: storage.getSuspectCache(),
      reactionCount: storage.getServerReactionCount(),
      scanCount: storage.getCounters().scans ?? 0,
    };
  }
}

export function bootSync() {
  if (bootPromise) return bootPromise;
  const skinType = storage.getSkinType();

  bootPromise = (async () => {
    // CRITICAL: resolve identity FIRST — if the cookie session is valid we
    // MUST NOT create a ghost anonymous user row on the server, and we MUST
    // NOT broadcast a stale-uid userStateSync payload.
    const userId = await resolveIdentity();
    try {
      await api.upsertUser({ id: userId, skin_type: skinType });
      storage.markUserSynced();
    } catch {
      // User sync failed — keep pending; still try state sync with cached data
    }
    try { await flushPendingScans(userId); } catch { /* ignore */ }
    try { await flushPendingReactions(userId); } catch { /* ignore */ }
    await syncUserState(userId);
    return { online: true, userId };
  })();

  return bootPromise;
}

export function resetBootSync() { bootPromise = null; identityPromise = null; }

export function useOnlineStatus() {
  return typeof navigator !== "undefined" ? navigator.onLine : true;
}
